<script setup lang="ts">
import { ref } from "vue";
import Dialog from "primevue/dialog";
import AutoComplete from "primevue/autocomplete";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { formatPrice, formatType } from "src/utils/recipe-formatters";
import type { FetchActionResponseLite } from "src/api/models/fetchActionResponseLite";

const visible = defineModel<boolean>("visible", { default: false });

const emit = defineEmits<{
  submit: [actionId: string];
}>();

const props = defineProps<{
  searchFn: (query: string) => Promise<FetchActionResponseLite[]>;
  linkedActionIds: string[];
}>();

const toast = useToast();
const selectedAction = ref<FetchActionResponseLite | null>(null);
const suggestions = ref<FetchActionResponseLite[]>([]);
const searching = ref(false);

async function onSearch(event: { query: string }) {
  searching.value = true;
  try {
    const results = await props.searchFn(event.query);
    suggestions.value = results.filter(
      (a) => a.id && !props.linkedActionIds.includes(a.id),
    );
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to search actions", life: 3000 });
    suggestions.value = [];
  } finally {
    searching.value = false;
  }
}

function onSubmit() {
  if (!selectedAction.value?.id) return;
  emit("submit", selectedAction.value.id);
  visible.value = false;
  selectedAction.value = null;
}

function onHide() {
  selectedAction.value = null;
  suggestions.value = [];
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    header="Link Existing Action"
    :modal="true"
    :style="{ width: '520px' }"
    @hide="onHide"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Search actions</label>
        <AutoComplete
          v-model="selectedAction"
          :suggestions="suggestions"
          option-label="name"
          placeholder="Type to search actions..."
          :loading="searching"
          dropdown
          force-selection
          class="w-full"
          @complete="onSearch"
        >
          <template #option="{ option }">
            <div class="flex justify-between items-center w-full gap-4">
              <span class="font-semibold">{{ option.name }}</span>
              <div class="flex gap-2 text-sm text-color-secondary">
                <span>{{ formatType(option.type) }}</span>
                <span>{{ formatPrice(option.price) }}</span>
              </div>
            </div>
          </template>
        </AutoComplete>
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" text @click="visible = false" />
      <Button
        label="Link"
        icon="pi pi-link"
        :disabled="!selectedAction?.id"
        @click="onSubmit"
      />
    </template>
  </Dialog>
</template>
